import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin'
import {
  $createTextNode,
  $getRoot,
  EditorState,
  Klass,
  LexicalEditor,
  LexicalNode,
} from 'lexical'
import React, { useEffect, useRef } from 'react'
import styled from 'styled-components'
import { RichTextConfig, RichTextContent } from 'types'

import { baseNodes, createBaseConfig } from './config'
import { lexicalEditorStyles } from './theme'

const EditorContainer = styled.div<{
  $minHeight: string
  $showToolbar: boolean
}>`
  position: relative;
  background: transparent;
  border-radius: 0;
  border: none;
  min-height: ${({ $minHeight }) => $minHeight};

  ${({ theme }) => lexicalEditorStyles({ theme })}
`

const PlaceholderDiv = styled.div<{ $showToolbar: boolean }>`
  position: absolute;
  top: ${({ $showToolbar }) => ($showToolbar ? '4rem' : '0.75rem')};
  left: 0.75rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  pointer-events: none;
  font-style: italic;
  user-select: none;
  z-index: 1;
`

const StyledContentEditable = styled(ContentEditable)<{
  $minHeight: string
  $showToolbar: boolean
}>`
  min-height: ${({ $minHeight }) => $minHeight};
  resize: none;
  outline: none;
  caret-color: ${({ theme }) => theme.forecast.defaultText};
  border-radius: 0;
`

/**
 * Props for the LexicalEditorWrapper component.
 *
 * - `value`: The content value of the editor.
 * - `onChange`: Callback triggered when the editor content changes.
 * - `className`: Custom className for the editor container.
 * - `nodes`: Additional custom nodes to include in the editor.
 * - `richText`: Whether to use RichTextPlugin (true) or PlainTextPlugin (false).
 * - `children`: Custom plugins to render inside the composer.
 * - `minHeight`: Minimum height for the editor container.
 * - `toolbar`: Optional toolbar component to render above the editor.
 */
export interface LexicalEditorWrapperProps extends RichTextConfig {
  value?: RichTextContent
  onChange?: (content: RichTextContent) => void
  className?: string
  nodes?: Array<Klass<LexicalNode>>
  richText?: boolean
  children?: React.ReactNode
  minHeight?: string
  toolbar?: React.ReactNode
}

export const LexicalEditorWrapper: React.FC<LexicalEditorWrapperProps> = ({
  value = '' as RichTextContent,
  onChange,
  placeholder = 'Start typing...',
  readOnly = false,
  autoFocus = false,
  className,
  nodes = [],
  richText = false,
  children,
  minHeight = '6rem',
  toolbar,
}) => {
  const hasSetInitialContent = useRef(false)

  const allNodes = [...baseNodes, ...nodes]

  const initialConfig = {
    ...createBaseConfig(placeholder, readOnly),
    nodes: allNodes,
  }

  const handleChange = (editorState: EditorState, editor: LexicalEditor) => {
    if (!onChange) return

    editorState.read(() => {
      if (editorState.isEmpty()) {
        onChange('' as RichTextContent)
        return
      }

      const htmlString = $generateHtmlFromNodes(editor)
      onChange(htmlString as RichTextContent)
    })
  }

  const Placeholder = () => (
    <PlaceholderDiv $showToolbar={Boolean(toolbar && richText)}>
      {placeholder}
    </PlaceholderDiv>
  )

  return (
    <EditorContainer
      className={className}
      $minHeight={minHeight}
      $showToolbar={Boolean(toolbar && richText)}>
      <LexicalComposer initialConfig={initialConfig}>
        <InitialContentPlugin
          value={value}
          hasSetInitialContent={hasSetInitialContent}
        />

        {toolbar}

        {richText ? (
          <RichTextPlugin
            contentEditable={
              <StyledContentEditable
                autoFocus={autoFocus}
                $minHeight={minHeight}
                $showToolbar={Boolean(toolbar && richText)}
              />
            }
            placeholder={<Placeholder />}
            ErrorBoundary={LexicalErrorBoundary}
          />
        ) : (
          <PlainTextPlugin
            contentEditable={
              <StyledContentEditable
                autoFocus={autoFocus}
                $minHeight={minHeight}
                $showToolbar={false}
              />
            }
            placeholder={<Placeholder />}
            ErrorBoundary={LexicalErrorBoundary}
          />
        )}

        <HistoryPlugin />
        <OnChangePlugin onChange={handleChange} />

        {richText && (
          <>
            <ListPlugin />
            <LinkPlugin />
            <MarkdownShortcutPlugin />
            <TabIndentationPlugin />
          </>
        )}

        {children}
      </LexicalComposer>
    </EditorContainer>
  )
}

// Plugin to set initial content only once (no constant syncing)
const InitialContentPlugin: React.FC<{
  value: RichTextContent
  hasSetInitialContent: React.MutableRefObject<boolean>
}> = ({ value, hasSetInitialContent }) => {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    if (!hasSetInitialContent.current && value && value.trim()) {
      hasSetInitialContent.current = true

      editor.update(() => {
        try {
          const parser = new DOMParser()
          const dom = parser.parseFromString(value, 'text/html')
          const nodes = $generateNodesFromDOM(editor, dom)
          const root = $getRoot()
          root.clear()
          root.append(...nodes)
        } catch (error) {
          console.warn('Failed to parse initial HTML content:', error)
          const root = $getRoot()
          root.clear()
          const textNode = $createTextNode(value)
          root.append(textNode)
        }
      })
    }
    // hasSetInitialContent is a ref (stable across renders) so we don't include it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, value])

  return null
}
